import { z } from 'zod4';

import { fixture } from './fixtures';

/**
 * RCS Schemas
 */

export enum CatalogItemState {
	Active = 'active',
	Archived = 'archived',
	Inactive = 'inactive',
}

export enum DenominationState {
	Active = 'active',
	Archived = 'archived',
	Inactive = 'inactive',
}

export enum DistributionMethod {
	Axomo = 'axomo',
	CashOut = 'cash_out',
	Custom = 'custom',
	Donation = 'donation',
	ExclusiveExperience = 'exclusive_experience',
	ExclusiveOther = 'exclusive_other',
	ExclusiveSwag = 'exclusive_swag',
	GiftCard = 'gift_card',
	Physical = 'physical',
}

export enum ItemType {
	Core = 'core',
	Exclusive = 'exclusive',
	Featured = 'featured',
	Swag = 'swag',
}

export enum Provider {
	Amazon = 'amazon',
	Bonusly = 'bonusly', // allowance boost
	BulkDonation = 'bulk_donation',
	Cadooz = 'cadooz', // deprecated
	Paypal = 'paypal', // deprecated
	Snappy = 'snappy',
	Tango = 'tango_card',
	Tremendous = 'tremendous',
}

export enum DenominationInactiveReasons {
	AdminDisabled = 'admin_disabled',
	Discontinued = 'discontinued',
	NeedsAttention = 'needs_attention',
	OutOfStock = 'out_of_stock',
	SystemDisabled = 'system_disabled',
}

export enum RedemptionState {
	New = 'new',
	Approved = 'approved',
	Fulfilled = 'fulfilled',
	Declined = 'declined',
	Refunded = 'refunded',
}

export enum RedemptionRecordState {
	New = 'new',
	ManualPending = 'manual_pending',
	ManualFulfilled = 'manual_fulfilled',
	Succeeded = 'succeeded',
	Failed = 'failed',
	Canceled = 'canceled',
	Retry = 'retry',
	OutOfStock = 'out_of_stock',
	PendingVoucher = 'pending_voucher',
	Expired = 'expired',
}

export enum CatalogItemType {
	Axomo = 'axomo',
	BuiltIn = 'built_in',
	Custom = 'custom',
	Other = 'other',
}

export const coreCategoryKeys = [
	'apparel',
	'beauty_and_spa',
	'cash',
	'donation',
	'electronics',
	'entertainment_and_electronics',
	'experiences_and_travel',
	'food_delivery',
	'food_and_drink',
	'gaming',
	'general',
	'gifts_and_services',
	'grocery',
	'health_and_wellness',
	'home_and_garden',
	'points_boost',
	'prepaid_card',
	'sports',
	'travel',
] as const;

export type CoreCategoryKey = (typeof coreCategoryKeys)[number];

export const TemporarilyUnavailableReasons = [
	DenominationInactiveReasons.AdminDisabled,
	DenominationInactiveReasons.NeedsAttention,
	DenominationInactiveReasons.SystemDisabled,
];

export const Reward = z.object({
	id: z.string(),
	brandName: z.string(),
	distributionMethod: z.enum(DistributionMethod),
	imageAltText: z.string(),
	imageUrl: z.string(),
	name: z.string(),
	minAmountInPoints: z.number().min(0),
	maxAmountInPoints: z.number().min(0),
	label: z.enum(['give_more', 'promoted', 'suggested', 'popular', 'exclusive']).nullable(),
	provider: z.enum(Provider).nullable(),
	type: z.enum(ItemType),
	/**
	 * Whether this reward requires rewards billing to be enabled.
	 * Points Boost, Company Exclusives, and non-Axomo Swag return false.
	 * Gift cards, donations, cash out, and Axomo items return true.
	 */
	requiresRewardsBilling: z.boolean().optional().default(true),
	subCategory: z.string().nullable().optional(),
});

export const Denomination = z.object({
	id: z.string(),
	description: z.string().nullable(),
	priceInSubunits: z.number(),
	quantity: z.number().nullable().optional(),
	inactiveReasons: z.array(z.enum(DenominationInactiveReasons)),
	optionName: z.string().nullable(),
	optionType: z.string().nullable(),
	materials: z.string().nullable(),
	features: z.string().nullable(),
	weight: z.string().nullable(),
});

export const DenominationDetail = Denomination.extend({
	denominationMedia: z.array(
		z.object({
			id: z.string(),
			altText: z.string(),
			imageUrl: z.url(),
		})
	),
	taxonomy: z
		.object({
			size: z.string().optional(),
			color: z.string().optional(),
		})
		.optional(),
});

export const CatalogItem = z.object({
	id: z.string(),
	brandName: z.string(),
	currency: z
		.object({
			id: z.string().optional(),
			exponent: z.number().optional(),
			iso4217Code: z.string(),
			name: z.string().optional(),
			symbol: z.string().optional(),
		})
		.nullable()
		.optional(),
	denominations: z.array(Denomination),
	distributionMethod: z.enum(DistributionMethod),
	feePercentage: z.number(),
	flatFeeInUsdCents: z.number(),
	fullName: z.string(),
	itemType: z.enum(['core', 'exclusive', 'featured', 'swag']),
	logo: z.object({
		id: z.string(),
		altText: z.string(),
		imageUrl: z.url(),
	}),
	minMaxPrice: z.array(z.number()),
	provider: z.enum(Provider).nullable().optional(),
	subCategories: z.array(z.string()),
});

export const CatalogItemDetail = z.object({
	brandName: z.string(),
	categories: z
		.array(
			z.object({
				id: z.string(),
				name: z.string(),
				key: z.string(),
			})
		)
		.nullable()
		.optional(),
	collectShippingInfo: z.boolean().optional(),
	currency: z
		.object({
			id: z.string().optional(),
			exponent: z.number().optional(),
			iso4217Code: z.string(),
			name: z.string().optional(),
			symbol: z.string().optional(),
		})
		.nullable()
		.optional(),
	description: z.string().nullable().optional(),
	distributionMethod: z.enum(DistributionMethod),
	feePercentage: z.number(),
	flatFeeInUsdCents: z.number(),
	fulfillmentPartner: z.string().optional().nullable(),
	fullName: z.string(),
	itemMedia: z.array(
		z.object({
			id: z.string(),
			altText: z.string(),
			imageUrl: z.url(),
		})
	),
	itemType: z.enum(ItemType),
	legacyId: z.string(),
	logo: z.object({
		id: z.string(),
		altText: z.string(),
		imageUrl: z.url(),
	}),
	provider: z.enum(Provider).nullable().optional(),
	/**
	 * Whether this reward requires rewards billing to be enabled.
	 * Points Boost and Company Exclusives return false.
	 * Gift cards, donations, cash out, and Axomo items return true.
	 */
	requiresRewardsBilling: z.boolean(),
	warnings: z.array(z.string()),
	website: z.string().nullable().optional(),
});

export const CompanyDenominationDetail = z.object({
	id: z.string(),
	legacyId: z.string(),
	denomination: DenominationDetail,
});

export const CompanyItem = z.object({
	id: z.string(),
	item: CatalogItem,
	label: z.enum(['forYou', 'popular', 'promoted', 'recognizeMore']).optional(),
});

export const CompanyItemDetail = z.object({
	id: z.string(),
	companyId: z.string(),
	state: z.enum(CatalogItemState),
	item: CatalogItemDetail,
	companyDenominations: z.array(CompanyDenominationDetail),
});

/**
 * Regular Rewards Schemas
 */

export const RedemptionRecord = z.object({
	id: z.string(),
	amount: z.number(),
	createdAt: z.string(),
	state: z.enum(RedemptionRecordState),
	transactionFee: z.number().nullable(),
});

export const RewardRedemption = z.object({
	id: z.string(),
	aasmState: z.enum(RedemptionState),
	brandName: z.string(),
	claimUrl: z.string().nullable(),
	color: z.string().nullable(),
	createdAt: z.string(),
	donation: z.boolean(),
	gift: z.boolean(),
	giftCard: z.boolean(),
	gifteeEmail: z.string().nullable(),
	name: z.string(),
	priceInCurrency: z.number(),
	priceInPoints: z.number(),
	size: z.string().nullable(),
	catalogItem: z.object({
		id: z.string(),
		denomination: z
			.object({
				id: z.string(),
				optionName: z.string(),
				optionType: z.string(),
				quantity: z.number().nullable(),
				distributionMethod: z.enum(DistributionMethod),
				provider: z.enum(Provider),
			})
			.nullable(),
		imageUrl: z.string(),
		type: z.enum(CatalogItemType),
	}),
	redemptionRecords: z.array(RedemptionRecord),
});

export const RewardRedemptionDetail = RewardRedemption.extend({
	shippingAddress: z
		.object({
			address1: z.string(),
			address2: z.string().nullable(),
			city: z.string(),
			state: z.string(),
			postalCode: z.string(),
			country: z.string(),
		})
		.nullable(),
});

/**
 * All schema type inferences
 */

export type CompanyItem = z.infer<typeof CompanyItem>;
export type CompanyItemDetail = z.infer<typeof CompanyItemDetail>;
export type CompanyDenominationDetail = z.infer<typeof CompanyDenominationDetail>;
export type Denomination = z.infer<typeof Denomination>;
export type DenominationDetail = z.infer<typeof DenominationDetail>;
export type RedemptionRecord = z.infer<typeof RedemptionRecord>;
export type RewardRedemption = z.infer<typeof RewardRedemption>;
export type RewardRedemptionDetail = z.infer<typeof RewardRedemptionDetail>;
export type Reward = z.infer<typeof Reward>;

/**
 * Helpers
 */

export const isPhysicalItem = (item: { item: { distributionMethod: DistributionMethod } }) => {
	return item.item.distributionMethod === DistributionMethod.Physical;
};

/**
 * Fixtures
 */

// Fixture functions using manual generators
export const redemptionRecordFixture = (overrides: Partial<RedemptionRecord> = {}): RedemptionRecord => ({
	id: fixture.id(),
	amount: fixture.int(100, 5000),
	createdAt: fixture.pastDate(7).toISOString(),
	state: RedemptionRecordState.Succeeded,
	transactionFee: fixture.boolean() ? fixture.int(5, 50) : null,
	...overrides,
});

export const rewardRedemptionFixture = (overrides: Partial<RewardRedemption> = {}): RewardRedemption => ({
	id: fixture.id(),
	aasmState: RedemptionState.Fulfilled,
	brandName: fixture.name('Brand'),
	claimUrl: fixture.boolean() ? fixture.url('claim') : null,
	color: fixture.boolean() ? 'Red' : null,
	createdAt: fixture.pastDate(30).toISOString(),
	donation: fixture.boolean(false),
	gift: fixture.boolean(false),
	giftCard: fixture.boolean(true),
	gifteeEmail: fixture.boolean() ? fixture.email('giftee') : null,
	name: fixture.name('Reward Item'),
	priceInCurrency: fixture.int(10, 100),
	priceInPoints: fixture.int(100, 1000),
	size: fixture.boolean() ? 'Medium' : null,
	catalogItem: {
		id: fixture.id(),
		denomination: fixture.boolean()
			? {
					id: fixture.id(),
					optionName: fixture.string('Option'),
					optionType: fixture.string('Type'),
					quantity: fixture.boolean() ? fixture.int(1, 100) : null,
					distributionMethod: DistributionMethod.GiftCard,
					provider: Provider.Tango,
				}
			: null,
		imageUrl: fixture.url('item-image'),
		type: CatalogItemType.BuiltIn,
	},
	redemptionRecords: [redemptionRecordFixture()],
	...overrides,
});

export const rewardRedemptionDetailFixture = (overrides: Partial<RewardRedemptionDetail> = {}): RewardRedemptionDetail => ({
	...rewardRedemptionFixture(),
	shippingAddress: fixture.boolean()
		? {
				address1: fixture.string('123 Main St'),
				address2: fixture.string('Apt 4B'),
				city: fixture.name('City'),
				state: fixture.string('CA'),
				postalCode: fixture.string('12345'),
				country: fixture.string('US'),
			}
		: null,
	...overrides,
});

export const denominationDetailFixture = (overrides: Partial<DenominationDetail> = {}): DenominationDetail => ({
	id: fixture.id(),
	description: fixture.string(),
	priceInSubunits: fixture.int(1000, 10000), // Price in cents/subunits
	quantity: fixture.boolean() ? fixture.int(1, 1000) : null,
	inactiveReasons: [],
	optionName: fixture.boolean() ? fixture.string('Option') : null,
	optionType: fixture.boolean() ? fixture.string('Type') : null,
	materials: fixture.boolean() ? fixture.string('Cotton') : null,
	features: fixture.boolean() ? fixture.string('Feature') : null,
	weight: fixture.boolean() ? fixture.string('1 lb') : null,
	denominationMedia: [
		{
			id: fixture.id(),
			altText: fixture.string('Denomination image'),
			imageUrl: fixture.url('media'),
		},
	],
	taxonomy: fixture.boolean()
		? {
				size: fixture.string('Medium'),
				color: fixture.string('Blue'),
			}
		: undefined,
	...overrides,
});

export const catalogItemDetailFixture = (overrides: Partial<z.infer<typeof CatalogItemDetail>> = {}): z.infer<typeof CatalogItemDetail> => ({
	brandName: fixture.name('Brand'),
	categories: fixture.boolean()
		? [
				{
					id: fixture.id(),
					name: fixture.name('Category'),
					key: fixture.string('category'),
				},
			]
		: null,
	collectShippingInfo: fixture.boolean(),
	currency: fixture.boolean()
		? {
				id: fixture.id(),
				exponent: 2,
				iso4217Code: 'USD',
				name: 'US Dollar',
				symbol: '$',
			}
		: null,
	description: fixture.boolean() ? fixture.description('catalog item') : null,
	distributionMethod: DistributionMethod.GiftCard,
	feePercentage: fixture.int(0, 10),
	flatFeeInUsdCents: fixture.int(0, 500),
	fulfillmentPartner: fixture.boolean() ? fixture.string('Partner') : null,
	fullName: fixture.name('Full Item Name'),
	itemMedia: [
		{
			id: fixture.id(),
			altText: fixture.string('Item image'),
			imageUrl: fixture.url('media'),
		},
	],
	itemType: ItemType.Core,
	legacyId: fixture.id(),
	logo: {
		id: fixture.id(),
		altText: fixture.string('Brand logo'),
		imageUrl: fixture.url('logo'),
	},
	provider: fixture.boolean() ? Provider.Tango : null,
	requiresRewardsBilling: true,
	warnings: [],
	website: fixture.boolean() ? fixture.url('website') : null,
	...overrides,
});

export const companyDenominationDetailFixture = (overrides: Partial<CompanyDenominationDetail> = {}): CompanyDenominationDetail => ({
	id: fixture.id(),
	legacyId: fixture.id(),
	denomination: denominationDetailFixture(),
	...overrides,
});

export const companyItemDetailFixture = (overrides: Partial<CompanyItemDetail> = {}): CompanyItemDetail => ({
	id: fixture.id(),
	companyId: fixture.id(),
	state: CatalogItemState.Active,
	item: catalogItemDetailFixture(),
	companyDenominations: [companyDenominationDetailFixture(), companyDenominationDetailFixture()],
	...overrides,
});
